import { ChangeEvent, EventHandler, KeyboardEvent, useCallback, useMemo, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { MonetaryVerificationsTableProps } from '../MonetaryVerificationTable.types'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useQuery } from '@percent/utility'

type PartnerSummary = {
  id: string
  name: string
}

type PartnerOption = {
  label: string
  value: string
}

const MAX_PARTNER_FILTER_IDS = 50

const isPartnerSummary = (value: unknown): value is PartnerSummary => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Partial<PartnerSummary>

  return typeof candidate.id === 'string' && typeof candidate.name === 'string'
}

const toPartnerOptions = (partners: unknown): PartnerOption[] =>
  Array.isArray(partners)
    ? partners.filter(isPartnerSummary).map(partner => ({ label: partner.name, value: partner.id }))
    : []

export const sanitizePartnerIds = (partnerIds: readonly (string | null)[] | null | undefined) =>
  [
    ...new Set(
      partnerIds?.map(partnerId => partnerId?.trim()).filter((partnerId): partnerId is string => !!partnerId) ?? []
    )
  ].slice(0, MAX_PARTNER_FILTER_IDS)

export const usePartnerFilter = ({
  queryParams,
  setQueryParams
}: Pick<MonetaryVerificationsTableProps, 'queryParams' | 'setQueryParams'>) => {
  const { adminService } = useServices()
  const [isOpen, setIsOpen] = useState(false)
  const [partnerSearch, setPartnerSearch] = useState('')
  const [debouncedPartnerSearch] = useDebounceValue(partnerSearch, 500)
  const selectedPartnerIds = useMemo(() => sanitizePartnerIds(queryParams.partnerIds), [queryParams.partnerIds])

  const { data: partnerSearchData, isLoading: isPartnerSearchLoading } = useQuery(
    ['/v1/admin/partners', 'monetary-verifications-filter', debouncedPartnerSearch],
    adminService.queries.getPartnerList,
    {
      query: debouncedPartnerSearch.trim() || undefined,
      pageSize: 25
    },
    { enabled: isOpen }
  )

  const { data: selectedPartnerData, isLoading: isSelectedPartnersLoading } = useQuery(
    ['/v1/admin/partners', 'monetary-verifications-filter-selected', selectedPartnerIds],
    adminService.queries.getPartnerList,
    {
      partnerIds: selectedPartnerIds,
      pageSize: 50
    },
    { enabled: selectedPartnerIds.length > 0 }
  )

  const partnerOptions = useMemo(() => toPartnerOptions(partnerSearchData), [partnerSearchData])
  const selectedPartnerOptions = useMemo(() => {
    const knownOptions = [...toPartnerOptions(selectedPartnerData), ...partnerOptions]

    return selectedPartnerIds.map(partnerId => {
      const knownOption = knownOptions.find(option => option.value === partnerId)

      return knownOption ?? { label: partnerId, value: partnerId }
    })
  }, [partnerOptions, selectedPartnerData, selectedPartnerIds])

  const onPartnerChange = useCallback(
    (partnerIds: string[]) => {
      const nextPartnerIds = sanitizePartnerIds(partnerIds)

      setQueryParams({ partnerIds: nextPartnerIds.length ? nextPartnerIds : undefined })
    },
    [setQueryParams]
  )

  const onPartnerSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPartnerSearch(event.target.value)
  }, [])

  const handleClearPartnerSearch = useCallback(() => setPartnerSearch(''), [])

  const handlePartnerSearchKeyDown: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(event => {
    if (event.key === 'Escape') {
      setPartnerSearch('')
    }
  }, [])

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)

    if (!open) {
      setPartnerSearch('')
    }
  }, [])

  return {
    partnerOptions,
    selectedPartnerIds,
    selectedPartnerOptions,
    onPartnerChange,
    isPartnerFilterLoading: isOpen && (isPartnerSearchLoading || isSelectedPartnersLoading),
    partnerSearch: {
      value: partnerSearch,
      onChange: onPartnerSearchChange,
      onClear: handleClearPartnerSearch,
      onKeyDown: handlePartnerSearchKeyDown
    },
    handleOpenChange
  }
}
